import { useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { useAutoGrow } from '@/hooks/useAutoGrow';
import { proseClasses } from '@/lib/markdown';

interface MarkdownEditorProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
  required?: boolean;
}

const tab = 'rounded-md px-2.5 py-1 text-xs font-semibold transition-colors';

// Textarea with an Escrever / Pré-visualizar toggle. The preview uses the very
// same react-markdown + proseClasses as the public news piece, so what you
// preview is what gets published. Auto-grows; no inner scrollbar.
export function MarkdownEditor({
  id,
  value,
  onChange,
  rows = 6,
  placeholder,
  required,
}: MarkdownEditorProps) {
  const [mode, setMode] = useState<'write' | 'preview'>('write');
  const ref = useRef<HTMLTextAreaElement>(null);
  useAutoGrow(ref, mode === 'write' ? value : '');

  return (
    <div className="border-line bg-surface focus-within:border-primary rounded-md border transition-all focus-within:shadow-[0_0_0_3px_rgb(from_var(--color-primary)_r_g_b/0.12)]">
      <div className="border-line flex items-center gap-1 border-b px-2 py-1.5">
        <button
          type="button"
          onClick={() => setMode('write')}
          className={`${tab} ${mode === 'write' ? 'bg-primary/10 text-primary' : 'text-on-surface-light hover:text-on-surface'}`}
        >
          Escrever
        </button>
        <button
          type="button"
          onClick={() => setMode('preview')}
          className={`${tab} ${mode === 'preview' ? 'bg-primary/10 text-primary' : 'text-on-surface-light hover:text-on-surface'}`}
        >
          Pré-visualizar
        </button>
        <span className="text-on-surface-light/60 ml-auto pr-1 text-[11px]">Markdown</span>
      </div>

      {mode === 'write' ? (
        <textarea
          id={id}
          ref={ref}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          required={required}
          placeholder={placeholder}
          className="text-on-surface placeholder:text-on-surface-light/70 block w-full resize-none overflow-hidden bg-transparent px-3.5 py-3 text-sm focus:outline-none"
        />
      ) : (
        <div className={`min-h-32 px-3.5 py-3 ${proseClasses}`}>
          {value.trim() ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
          ) : (
            <p className="text-on-surface-light text-sm italic">Nada para pré-visualizar.</p>
          )}
        </div>
      )}
    </div>
  );
}
